import { useMemo, useState, useEffect, useRef } from 'react';
import useAuth from '@sdk/authentication/useAuth';
import useToast from '@sdk/common/toast/useToast';
import { isUnscheduledShift } from 'sling-sdk/src/automatedBreaks/utils';
import usePromiseModalState from '@sdk/components/ui/modal/usePromiseModalState';

import { FormSubmitStatuses } from '@web/forms/types';
import { UseEditTimesheetModalProps } from '@web/timesheets/EditTimesheetModal/editTimesheetModal.types';
import { useAutomatedBreaksSettings } from '@web/settings/components/schedule/Breaks/breaks.hooks';
import { useEditTimesheetModalForm } from '@web/timesheets/EditTimesheetModal/editTimesheetModal.form';
import { validateEditTimesheetForm } from '@web/timesheets/EditTimesheetModal/utils/validation';
import {
  extractStartDateWithLocalTime,
  getBreakAttestationsFromTimesheetProjections,
} from '@web/timesheets/EditTimesheetModal/editTimesheetModal.utils';
import { useTimesheetProjectionsWithAttstations } from '@web/timesheets/TimesheetViewModal/timesheetViewModal.hooks';
import { useTimesheetHistory } from '@web/timesheets/hooks/useTimesheetHistory';
import { shouldDisplayAttestations } from '@web/timesheets/TimesheetViewModal/timesheetViewModal.utils';

export const useEditTimesheetModal = ({ onSave, shift }: UseEditTimesheetModalProps) => {
  const [submitErrors, setSubmitErrors] = useState(null);
  const [isHistoryView, setIsHistoryView] = useState(false);
  const { formData, formValues, submit } = useEditTimesheetModalForm({ shift });
  const { currentUser } = useAuth();
  const { automatedBreaks } = useAutomatedBreaksSettings();
  const [canSubmit, setCanSubmit] = useState(false);
  const { showErrorToast } = useToast();
  const initialFormValuesRef = useRef(null);
  const { historyItems, fetchHistory } = useTimesheetHistory(shift.id, shift.user.id);
  const confirmExcessiveBreaksModalState = usePromiseModalState<boolean>();
  const chooseAttestationToReplaceModalState = usePromiseModalState<
    number,
    {
      breakType: string;
      breakNumbers: number[];
    }
  >();

  const modalConfig = { hideClose: true, noDismiss: true };

  useEffect(() => {
    if (!initialFormValuesRef.current) {
      initialFormValuesRef.current = formValues;
    }

    const validationResults = validateEditTimesheetForm(formValues);
    const isFormValid = !Object.keys(validationResults).length;
    const isDirty = formValues !== initialFormValuesRef.current;

    setCanSubmit(isFormValid && isDirty);
  }, [formValues]);

  const timesheetProjectionsWithAttstations = useTimesheetProjectionsWithAttstations(
    shift.timesheetProjections
  );

  const breakAttestations = useMemo(
    () => getBreakAttestationsFromTimesheetProjections(timesheetProjectionsWithAttstations),
    [timesheetProjectionsWithAttstations]
  );

  const addEntry = () => {
    formData.dynamicLists.timeClock.addItem();
  };

  const handleSubmit = async () => {
    try {
      await submit();
      onSave({ shiftId: shift.id, userId: shift.user.id });
    } catch (e) {
      if (
        e.status === FormSubmitStatuses.fail &&
        e.errors?.formErrors &&
        Object.keys(e.errors.formErrors).length
      ) {
        setSubmitErrors(e.errors.formErrors);
      } else {
        showErrorToast(e.errors);
      }

      // Trigger X animation on progress button
      throw e;
    }
  };

  const toggleHistoryView = () => {
    if (!isHistoryView) {
      fetchHistory();
    }

    setIsHistoryView(!isHistoryView);
  };

  const isAttestationVisible = shouldDisplayAttestations(shift);
  const scheduledShiftStart = extractStartDateWithLocalTime(shift);
  const isUnscheduled = isUnscheduledShift(shift);

  return {
    modalConfig,
    canSubmit,
    handleSubmit,
    submitErrors,
    setSubmitErrors,
    fields: formData.fields,
    dynamicLists: formData.dynamicLists,
    automatedBreaks,
    usTimeFormat: currentUser.settings.usTimeFormat,
    isAttestationVisible,
    timesheetProjectionsWithAttstations,
    breakAttestations,
    addEntry,
    isHistoryView,
    toggleHistoryView,
    historyItems,
    scheduledShiftStart,
    isUnscheduled,
    confirmExcessiveBreaksModalState,
    chooseAttestationToReplaceModalState,
  };
};
